package com.cs.ejobin.ui.screens.auth

import androidx.compose.foundation.Image
import androidx.compose.foundation.layout.Arrangement
import androidx.compose.foundation.layout.Column
import androidx.compose.foundation.layout.Row
import androidx.compose.foundation.layout.Spacer
import androidx.compose.foundation.layout.fillMaxSize
import androidx.compose.foundation.layout.fillMaxWidth
import androidx.compose.foundation.layout.height
import androidx.compose.foundation.layout.padding
import androidx.compose.foundation.layout.size
import androidx.compose.foundation.layout.width
import androidx.compose.foundation.layout.widthIn
import androidx.compose.foundation.text.KeyboardActions
import androidx.compose.foundation.text.KeyboardOptions
import androidx.compose.material3.Button
import androidx.compose.material3.ButtonDefaults
import androidx.compose.material3.Card
import androidx.compose.material3.CardDefaults
import androidx.compose.material3.CircularProgressIndicator
import androidx.compose.material3.MaterialTheme
import androidx.compose.material3.OutlinedTextField
import androidx.compose.material3.Text
import androidx.compose.material3.TextButton
import androidx.compose.runtime.Composable
import androidx.compose.runtime.collectAsState
import androidx.compose.runtime.getValue
import androidx.compose.runtime.mutableStateOf
import androidx.compose.runtime.remember
import androidx.compose.runtime.setValue
import androidx.compose.ui.Alignment
import androidx.compose.ui.Modifier
import androidx.compose.ui.text.input.ImeAction
import androidx.compose.ui.text.input.KeyboardType
import androidx.compose.ui.text.input.PasswordVisualTransformation
import androidx.compose.ui.text.input.VisualTransformation
import androidx.compose.ui.unit.dp
import com.cs.ejobin.i18n.Keys
import com.cs.ejobin.i18n.t
import com.cs.ejobin.ui.components.ErrorText
import com.cs.ejobin.ui.components.ScreenScaffold
import com.cs.ejobin.ui.theme.AppColors
import com.cs.ejobin.ui.theme.AppSurfaces
import com.cs.ejobin.vm.LoginViewModel
import ejobin.composeapp.generated.resources.Res
import ejobin.composeapp.generated.resources.eJobin_logo
import org.jetbrains.compose.resources.painterResource

@Composable
fun LoginScreen(
    vm: LoginViewModel,
    onGoSignup: () -> Unit,
    onGoForgot: () -> Unit,
    onLoggedIn: () -> Unit
) {
    val state by vm.state.collectAsState()
    var passwordVisible by remember { mutableStateOf(false) }
    val primaryTextButtonColors = ButtonDefaults.textButtonColors(
        contentColor = AppColors.Primary
    )

    ScreenScaffold(
        title = "",
        scrollable = true,
        topActions = {}
    ) {
        Column(
            modifier = Modifier.fillMaxSize(),
            horizontalAlignment = Alignment.CenterHorizontally
        ) {
            Spacer(Modifier.height(18.dp))

            Image(
                painter = painterResource(Res.drawable.eJobin_logo),
                contentDescription = "eJobin",
                modifier = Modifier.size(120.dp)
            )

            Spacer(Modifier.height(18.dp))

            Card(
                modifier = Modifier
                    .fillMaxWidth()
                    .widthIn(max = 520.dp),
                colors = AppSurfaces.glassCard(),
                border = CardDefaults.outlinedCardBorder()
            ) {
                Column(
                    modifier = Modifier.padding(18.dp),
                    verticalArrangement = Arrangement.spacedBy(12.dp),
                    horizontalAlignment = Alignment.CenterHorizontally
                ) {
                    Text(
                        text = t(Keys.home_tagline),
                        style = MaterialTheme.typography.titleLarge,
                        color = AppColors.Primary
                    )

                    Text(
                        text = t(Keys.login_title),
                        style = MaterialTheme.typography.titleLarge,
                        color = AppColors.TextPrimary
                    )

                    Text(
                        text = t(Keys.login_subtitle),
                        style = MaterialTheme.typography.bodyMedium,
                        color = AppColors.TextPrimary
                    )

                    Spacer(Modifier.height(6.dp))

                    OutlinedTextField(
                        value = state.email,
                        onValueChange = vm::onEmailChange,
                        modifier = Modifier.fillMaxWidth(),
                        singleLine = true,
                        enabled = !state.isLoading,
                        label = { Text(t(Keys.email)) },
                        keyboardOptions = KeyboardOptions(
                            keyboardType = KeyboardType.Email,
                            imeAction = ImeAction.Next
                        )
                    )

                    OutlinedTextField(
                        value = state.password,
                        onValueChange = vm::onPasswordChange,
                        modifier = Modifier.fillMaxWidth(),
                        singleLine = true,
                        enabled = !state.isLoading,
                        label = { Text(t(Keys.password)) },
                        visualTransformation = if (passwordVisible) {
                            VisualTransformation.None
                        } else {
                            PasswordVisualTransformation()
                        },
                        trailingIcon = {
                            TextButton(
                                onClick = { passwordVisible = !passwordVisible },
                                enabled = !state.isLoading,
                                colors = primaryTextButtonColors
                            ) {
                                Text(if (passwordVisible) t(Keys.hide) else t(Keys.show))
                            }
                        },
                        keyboardOptions = KeyboardOptions(
                            keyboardType = KeyboardType.Password,
                            imeAction = ImeAction.Done
                        ),
                        keyboardActions = KeyboardActions(
                            onDone = {
                                if (!state.isLoading) vm.login(onSuccess = onLoggedIn)
                            }
                        )
                    )

                    ErrorText(state.errorKey)

                    Button(
                        onClick = { vm.login(onSuccess = onLoggedIn) },
                        modifier = Modifier.fillMaxWidth(),
                        enabled = !state.isLoading,
                        colors = ButtonDefaults.buttonColors(containerColor = AppColors.Primary)
                    ) {
                        if (state.isLoading) {
                            CircularProgressIndicator(
                                modifier = Modifier.size(18.dp),
                                strokeWidth = 2.dp,
                                color = MaterialTheme.colorScheme.onPrimary
                            )
                            Spacer(Modifier.width(10.dp))
                            Text(t(Keys.loading))
                        } else {
                            Text(t(Keys.login))
                        }
                    }

                    Row(
                        modifier = Modifier.fillMaxWidth(),
                        horizontalArrangement = Arrangement.SpaceBetween,
                        verticalAlignment = Alignment.CenterVertically
                    ) {
                        TextButton(
                            onClick = onGoForgot,
                            enabled = !state.isLoading,
                            colors = primaryTextButtonColors
                        ) {
                            Text(t(Keys.forgot_password))
                        }

                        TextButton(
                            onClick = onGoSignup,
                            enabled = !state.isLoading,
                            colors = primaryTextButtonColors
                        ) {
                            Text(t(Keys.create_account))
                        }
                    }
                }
            }

            Spacer(Modifier.height(18.dp))
        }
    }
}